import React, { useEffect, useMemo, useState } from 'react';

import { loadExchangeRates } from './api';
import { useXomicsAuth } from './AuthContext';
import { tx } from './i18n';

// Money in the currency a person reads the Business pages in (their profile's view currency),
// beside what was charged. Nothing charged is converted: a total over several currencies, or in
// another currency, is shown once more in the view currency, at the European Central Bank's rate
// of the day, and says so. A currency without a rate is left out of that total and named.

// One reading of the rates for the whole portal, read again after an hour.
let cached = null;
function ratesOnce() {
  if (cached === null || Date.now() - cached.at > 60 * 60 * 1000) {
    const promise = loadExchangeRates().catch((error) => { cached = null; throw error; });
    cached = { at: Date.now(), promise };
  }
  return cached.promise;
}

export function useExchangeRates(wanted = true) {
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    if (!wanted) return undefined;
    let live = true;
    ratesOnce().then((data) => { if (live) setState({ data, error: null }); }, (error) => { if (live) setState({ data: null, error }); });
    return () => { live = false; };
  }, [wanted]);
  return state;
}

// An amount in its own currency, with its minor units.
export function moneyText(minor, currency, minorUnit) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: minorUnit, maximumFractionDigits: minorUnit })
      .format(Number(minor) / 10 ** minorUnit);
  } catch (_error) {
    return `${(Number(minor) / 10 ** minorUnit).toFixed(minorUnit)} ${currency}`;
  }
}

const rateDay = (ms) => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(ms));

// The person's view currency, and a total of amounts ({ minor, currency, minorUnit }) in it: null
// when they read as charged, or when every amount is already in that currency.
export function useViewMoney() {
  const { user } = useXomicsAuth();
  const code = user?.preferredCurrency ?? null;
  const { data, error } = useExchangeRates(code !== null);
  return useMemo(() => {
    const byCode = new Map((data?.currencies ?? []).map((currency) => [currency.code, currency]));
    const target = code === null ? null : byCode.get(code) ?? null;
    function total(amounts) {
      const shown = amounts.filter((amount) => amount && amount.minor !== null && amount.minor !== undefined);
      if (code === null || shown.length === 0 || shown.every((amount) => amount.currency === code)) return null;
      if (target === null || target.unitsPerEur === null) return { minor: null, currency: code, minorUnit: 2, missing: [code], rateDate: null };
      let euros = 0;
      const missing = [];
      let day = null;
      for (const amount of shown) {
        const from = byCode.get(amount.currency);
        if (!from || from.unitsPerEur === null) { if (!missing.includes(amount.currency)) missing.push(amount.currency); continue; }
        euros += (Number(amount.minor) / 10 ** Number(amount.minorUnit)) / from.unitsPerEur;
        day = Math.max(day ?? 0, from.rateDate ?? 0, target.rateDate ?? 0);
      }
      return { minor: Math.round(euros * target.unitsPerEur * 10 ** target.minorUnit), currency: code, minorUnit: target.minorUnit, missing, rateDate: day };
    }
    return { code, total, ready: data !== null || code === null, error };
  }, [code, data, error]);
}

// A total over amounts in their own currencies, as one figure: in the view currency when one is
// chosen and the amounts are not all in it already (`converted`, with what was charged as a note
// and the rate's day as its title), otherwise each currency's sum, one after another.
export function useMoneyTotal() {
  const view = useViewMoney();
  return (amounts) => {
    const byCurrency = new Map();
    for (const amount of amounts.filter((entry) => entry && entry.minor !== null && entry.minor !== undefined)) {
      const had = byCurrency.get(amount.currency) || { minor: 0, minorUnit: amount.minorUnit };
      had.minor += Number(amount.minor);
      byCurrency.set(amount.currency, had);
    }
    const charged = [...byCurrency.entries()].map(([currency, entry]) => moneyText(entry.minor, currency, entry.minorUnit));
    const converted = view.total(amounts);
    if (converted && converted.minor !== null && converted.missing.length === 0) {
      return {
        text: moneyText(converted.minor, converted.currency, converted.minorUnit), converted: true,
        note: tx('ui.money.as_charged', { amounts: charged.join(' · ') }),
        title: tx('ui.money.in_view', { currency: converted.currency, date: converted.rateDate ? rateDay(converted.rateDate) : '' }),
      };
    }
    return { text: charged.length ? charged.join(' · ') : null, converted: false, note: null, title: null };
  };
}

// A total in the view currency, under the amounts as charged: "≈ €4,065.20", with the rate's day.
export function InView({ amounts, className = '' }) {
  const view = useViewMoney();
  const converted = view.total(amounts);
  if (converted === null) return null;
  if (view.error) return <small className={`xo-in-view is-missing ${className}`}>{tx('ui.money.rates_missing')}</small>;
  if (converted.minor === null) return <small className={`xo-in-view is-missing ${className}`}>{tx('ui.money.no_rate', { currencies: converted.missing.join(', ') })}</small>;
  const note = tx('ui.money.in_view', { currency: converted.currency, date: converted.rateDate ? rateDay(converted.rateDate) : '' });
  return <small className={`xo-in-view ${className}`} title={note}>
    {tx('ui.money.in_view_short', { amount: moneyText(converted.minor, converted.currency, converted.minorUnit) })}
    {converted.missing.length > 0 && <span> · {tx('ui.money.no_rate', { currencies: converted.missing.join(', ') })}</span>}
  </small>;
}
